import {
  getGradientBounds,
  getLinearGradientPoints,
  normalizeGradientTurn,
} from '@web-stories-wp/patterns';

// CSS percentages are radii; legacy SVG text already uses full diameters.
// Missing CSS size means farthest-corner, rather than a 100% diameter.
export function upgradeRadialGeometry(pattern, legacyPaint = 'css') {
  if (pattern.type !== 'radial' || pattern.geometryVersion === 2)
    return pattern;
  const center = pattern.center || { x: 0.5, y: 0.5 };
  const size =
    legacyPaint === 'svg'
      ? pattern.size || { w: 1, h: 1 }
      : pattern.size
      ? { w: 2 * pattern.size.w, h: 2 * pattern.size.h }
      : legacyPaint === 'filter' && pattern.rotation
      ? { w: 2, h: 2 }
      : {
          w:
            2 *
            Math.SQRT2 *
            Math.max(Math.abs(center.x), Math.abs(1 - center.x)),
          h:
            2 *
            Math.SQRT2 *
            Math.max(Math.abs(center.y), Math.abs(1 - center.y)),
        };
  return {
    ...pattern,
    size,
    geometryVersion: 2,
    // Only filter CSS rotated legacy radial paint; ordinary CSS and SVG did not.
    rotation: legacyPaint === 'filter' ? pattern.rotation || 0 : 0,
  };
}

export function getGradientRotation(pattern, bounds = {}) {
  if (pattern.type !== 'linear' || !pattern.start || !pattern.end) {
    return normalizeGradientTurn(pattern.rotation || 0);
  }
  const { start, end } = getLinearGradientPoints(pattern, bounds);
  if (start.x === end.x && start.y === end.y)
    return normalizeGradientTurn(pattern.rotation || 0);
  return normalizeGradientTurn(
    Math.atan2(end.y - start.y, end.x - start.x) / (Math.PI * 2) - 0.25
  );
}

export function updateGradientGeometry(
  pattern,
  { property, value, bounds = {}, legacyPaint = 'css' }
) {
  if (!Number.isFinite(value)) return pattern;
  const box = getGradientBounds(bounds);
  if (property === 'rotation') {
    const rotation = normalizeGradientTurn(value);
    const previous = getGradientRotation(pattern, box);
    if (Math.abs(rotation - previous) < 1e-8) return pattern;
    if (pattern.type !== 'linear' || !pattern.start || !pattern.end) {
      return {
        ...upgradeRadialGeometry(pattern, legacyPaint),
        rotation,
        geometryVersion: 2,
      };
    }
    const { start, end } = getLinearGradientPoints(pattern, box);
    const cx = (start.x + end.x) / 2;
    const cy = (start.y + end.y) / 2;
    const angle = (rotation - previous) * Math.PI * 2;
    const rotate = (point) => ({
      x:
        (cx +
          (point.x - cx) * Math.cos(angle) -
          (point.y - cy) * Math.sin(angle) -
          box.x) /
        box.width,
      y:
        (cy +
          (point.x - cx) * Math.sin(angle) +
          (point.y - cy) * Math.cos(angle) -
          box.y) /
        box.height,
    });
    return {
      ...pattern,
      start: rotate(start),
      end: rotate(end),
      rotation,
      geometryVersion: 2,
    };
  }
  const [target, dimension] = property.split('.');
  if (
    pattern.type === 'linear' &&
    ['start', 'end'].includes(target) &&
    ['x', 'y'].includes(dimension)
  ) {
    const points = getLinearGradientPoints(pattern, box);
    const normalized = (point) => ({
      x: (point.x - box.x) / box.width,
      y: (point.y - box.y) / box.height,
    });
    const next = {
      ...pattern,
      start: normalized(points.start),
      end: normalized(points.end),
    };
    if (Math.abs(next[target][dimension] - value) < 1e-8) return pattern;
    next[target] = { ...next[target], [dimension]: value };
    return {
      ...next,
      rotation: getGradientRotation(next, box),
      geometryVersion: 2,
    };
  }
  if (
    pattern.type === 'radial' &&
    target === 'center' &&
    ['x', 'y'].includes(dimension)
  ) {
    const center = pattern.center || { x: 0.5, y: 0.5 };
    return center[dimension] === value
      ? pattern
      : {
          ...upgradeRadialGeometry(pattern, legacyPaint),
          center: { ...center, [dimension]: value },
          geometryVersion: 2,
        };
  }
  if (
    pattern.type === 'radial' &&
    target === 'size' &&
    ['w', 'h'].includes(dimension) &&
    value > 0
  ) {
    const size = pattern.size || { w: 1, h: 1 };
    const upgraded = upgradeRadialGeometry(pattern, legacyPaint);
    return size[dimension] === value
      ? pattern
      : {
          ...upgraded,
          size: {
            w: upgraded.size?.w ?? 1,
            h: upgraded.size?.h ?? 1,
            [dimension]: value,
          },
          geometryVersion: 2,
        };
  }
  return pattern;
}
